import React, { cloneElement, isValidElement, useCallback, useEffect, useRef } from 'react';

import { showDataGridTooltip, hideDataGridTooltip } from '../utils/createTooltipPopper';
import { getDataGridTooltipElement } from '../utils/getDataGridTooltipElement';
import {
    applyStoredScrollbarOffset,
    clearHoveredRowActions,
    restoreActionsWrapperVisibility,
} from '../utils/actionsOverlay';
import { createUseStyles } from '@Sage/hoi-poi-ui';

import { intercomHideAndShow } from 'utils/custom';

import styles from './styles';
const useStyles = createUseStyles(styles, 'Actions');

const PLACEMENTS = { left: 'top-start', center: 'top', right: 'top-end', bottom: 'bottom' };

const Actions = ({
    actions,
    hideActions,
    hoveredRowInfo,
    id,
    dataGridRef,
    rowData,
    isLazyLoaded,
    intercomRef,
    isLoading,
    forceActionsRefresh,
    clearHoveredRow,
}) => {
    const classes = useStyles();
    const actionsContainerRef = useRef(null);
    const interactionReleaseFrame = useRef(null);
    const containerClasses = useRef();
    containerClasses.current = [classes.actionsContainer];

    if (hideActions) containerClasses.current.push(classes.hideActions);

    const containerId = id ? `w5-actions-component-${id}` : 'w5-actions-component';

    const clearRowHoverClasses = useCallback(() => {
        const table =
            dataGridRef || actionsContainerRef.current?.closest('[class*=SectionList__root]');
        const rows = table?.querySelectorAll('.ag-row--action-hover') ?? [];
        for (let row of rows) {
            row.classList?.remove('ag-row--action-hover');
        }
    }, [dataGridRef]);

    const handleFloatingInteractionChange = useCallback(
        (isActive) => {
            const container = actionsContainerRef.current;
            if (!container) return;

            window.cancelAnimationFrame(interactionReleaseFrame.current);

            if (isActive) {
                delete container.dataset.floatingInteractionClosing;
                container.dataset.floatingInteractionActive = 'true';
                return;
            }

            container.dataset.floatingInteractionClosing = 'true';
            interactionReleaseFrame.current = window.requestAnimationFrame(() => {
                delete container.dataset.floatingInteractionActive;
                if (!container.matches(':hover')) {
                    container.style.display = 'none';
                    clearRowHoverClasses();
                }
                interactionReleaseFrame.current = window.requestAnimationFrame(() => {
                    delete container.dataset.floatingInteractionClosing;
                });
            });
        },
        [clearRowHoverClasses],
    );

    useEffect(
        () => () => {
            window.cancelAnimationFrame(interactionReleaseFrame.current);
        },
        [],
    );

    useEffect(() => {
        const actionsContainer = document.getElementById(containerId);
        if (!actionsContainer || !hoveredRowInfo) return;

        if (hoveredRowInfo.scrollbarWidth > 0) {
            actionsContainer.dataset.scrollbarWidth = String(hoveredRowInfo.scrollbarWidth);
            applyStoredScrollbarOffset(actionsContainer);
        } else if (!hoveredRowInfo.hasScroll) {
            actionsContainer.dataset.scrollbarWidth = '0';
            applyStoredScrollbarOffset(actionsContainer);
        }
    }, [containerId, hoveredRowInfo]);

    useEffect(() => {
        // IF forceActionsRefresh is enabled:
        // Reenable the mouse events in the actions container if they were disabled
        const isDisabled = containerClasses.current.indexOf(classes.disabled);
        if (forceActionsRefresh && !isLoading && isDisabled !== -1) {
            setTimeout(() => {
                containerClasses.current.splice(isDisabled, 1);
            });
        }

        if (dataGridRef) {
            const tooltip = getDataGridTooltipElement(dataGridRef.id);
            tooltip && (tooltip.style.display = 'none');
        }
        // Reloading when rowData or isLazyLoaded changes
    }, [
        actions,
        dataGridRef,
        rowData,
        isLazyLoaded,
        classes.disabled,
        isLoading,
        forceActionsRefresh,
        hoveredRowInfo?.data,
    ]);

    const onMouseClick = useCallback(() => {
        // IF forceActionsRefresh is enabled:
        // Disable mouse events in the actions container so it triggers the mouseEnter event in the underlying table cell and the hovered row actions are updated
        forceActionsRefresh && containerClasses.current.push(classes.disabled);
    }, [classes.disabled, containerClasses, forceActionsRefresh]);

    const onMouseEnter = useCallback(
        (e) => {
            restoreActionsWrapperVisibility(e.currentTarget);
            const table = dataGridRef || e.target.closest('[class*=SectionList__root]');
            const rows = table?.querySelectorAll('.ag-row--action-hover') ?? [];
            for (let row of rows) {
                row.classList?.remove('ag-row--action-hover');
            }
            hoveredRowInfo?.rowElement?.classList?.add('ag-row--action-hover');
        },
        [dataGridRef, hoveredRowInfo?.rowElement?.classList],
    );

    const onMouseLeave = useCallback(
        (e) => {
            if (intercomRef) intercomHideAndShow('show');
            if (e.currentTarget.dataset.floatingInteractionActive === 'true') return;
            const table = dataGridRef || e.target.closest('[class*=SectionList__root]');
            const rows = table?.querySelectorAll('.ag-row--action-hover') ?? [];
            for (let row of rows) {
                row.classList?.remove('ag-row--action-hover');
            }
            const actionsContainer = document.getElementById(containerId);
            clearHoveredRowActions({ getHoveredRowInfo: clearHoveredRow }, actionsContainer);
        },
        [clearHoveredRow, containerId, dataGridRef, intercomRef],
    );

    const onMouseEnterIcon = useCallback(
        (e, data) => {
            if (intercomRef) intercomHideAndShow(intercomRef);
            e.preventDefault();
            e.stopPropagation();
            if (!data) return;

            const target = e.target.closest('[class*=actionsIcon]');
            const table = dataGridRef || e.target.closest('[class*=SectionList__root]');
            const tooltip = getDataGridTooltipElement(table?.id);
            const actionsContainer = e.target.closest('[class*=actionsContainer]');

            if (!tooltip) return;

            hoveredRowInfo?.rowElement?.classList?.add('ag-row--action-hover');
            restoreActionsWrapperVisibility(actionsContainer);
            tooltip.firstElementChild.innerHTML = data.content;

            showDataGridTooltip(target, tooltip, {
                placement: PLACEMENTS[data.placement] || PLACEMENTS.center,
            });
        },
        [dataGridRef, hoveredRowInfo, intercomRef],
    );

    const onMouseLeaveIcon = useCallback(
        (e) => {
            const table = dataGridRef || e.target.closest('[class*=SectionList__root]');
            const tooltip = getDataGridTooltipElement(table?.id);
            if (!tooltip) return;
            hideDataGridTooltip(tooltip);
        },
        [dataGridRef],
    );

    return (
        <div
            id={containerId}
            ref={actionsContainerRef}
            className={[containerClasses.current.join(' ')]}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {hoveredRowInfo?.data && (
                <div className={classes.actionsContent}>
                    {actions.map((current, index) => {
                        const action = current(hoveredRowInfo.data);
                        if (!action) return null;
                        const canTrackFloatingInteraction =
                            isValidElement(action.icon) &&
                            (typeof action.icon.type === 'function' ||
                                (typeof action.icon.type === 'object' &&
                                    action.icon.type !== null));
                        const icon = canTrackFloatingInteraction
                            ? cloneElement(action.icon, {
                                  onFloatingInteractionChange: handleFloatingInteractionChange,
                              })
                            : action.icon;
                        return (
                            <div
                                key={index}
                                className={classes.actionsIcon}
                                onMouseEnter={(e) => onMouseEnterIcon(e, action.tooltip)}
                                onMouseLeave={onMouseLeaveIcon}
                                onClick={onMouseClick}
                            >
                                {icon}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default React.memo(Actions);
